import React, { useMemo } from 'react';
import {
  IconCircle,
  IconClipboardType,
  IconCloseButton,
  IconEye,
  IconFlag,
  IconMark,
  IconStopwatch,
  OrangeButton,
  SmallButton,
} from 'components';
import { COLORS } from 'utils/colors';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'api/constants';
import { getClientTaskProgressState } from 'utils/helpers';

export const SingleClientTaskRow = ({
  task,
  weekIndex,
  granted,
  lastAssignedWeek,
  lastAssignedAt,
}) => {
  const generatedDate = task.assigned_at ? dayjs(task.assigned_at) : null;
  const taskState = useMemo(
    () => getClientTaskProgressState(task, weekIndex, lastAssignedWeek, lastAssignedAt),
    [lastAssignedAt, lastAssignedWeek, task, weekIndex],
  );
  // const initialDate = dayjs(task.updated_at);

  const storeTaskButtons = useMemo(() => {
    if (task.completed) {
      return (
        <SmallButton
          btnClassName="ant-btn-mobile !h-[44px] md:!h-[34px] px-4 md:!px-3"
          icon={<IconEye className="inline-block md:hidden hover:text-darkGrey" color={COLORS.blue} />}
          label="View"
          href={`/weekly-forms/${weekIndex}`}
        />
      );
    } else if (task.task_id) {
      return (
        <OrangeButton
          disabled={false}
          href={`/weekly-forms/${weekIndex}`}
          btnClassName="ant-btn-green ant-btn-mobile font-semibold text-16 md:!text-13 rounded-[10px] md:rounded-lg !h-[44px] md:!h-[34px] px-6 md:!px-3"
          label="Open"
          icon={<IconClipboardType color="#fff" className="inline-block md:hidden min-w-5 min-h-5" />}
        />
      );
    }
    return (
      <OrangeButton
        disabled={true}
        btnClassName="ant-btn-mobile !bg-grey !text-darkGrey border-none shadow-none font-semibold text-16 md:!text-13 rounded-[10px] md:rounded-lg !h-[44px] md:!h-[34px] px-6 md:!px-3 cursor-not-allowed opacity-60"
        label="Open"
        icon={<IconClipboardType color="#8A8D96" className="inline-block md:hidden min-w-5 min-h-5" />}
      />
    );
  }, [task.completed, task.task_id, weekIndex]);

  const statusBg = () => {
    switch (taskState) {
      case 'completed':
        return 'bg-green text-white';
      case 'pending':
        return 'bg-orange text-white';
      case 'overdue':
        return 'bg-red text-white';
      case 'current':
        return 'bg-grey text-darkGrey ring-2 ring-inset ring-orange';
      default:
        return 'bg-grey text-darkGrey';
    }
  };

  const iconType = (isMobile = false) => {
    const iconClass = isMobile ? 'min-w-4 w-4 h-4' : 'min-w-6 w-6 h-6';
    if (task.completed) return <IconMark color={COLORS.white} className={iconClass} />;
    if (taskState === 'pending') return <IconStopwatch className={iconClass} color={COLORS.white} />;
    if (taskState === 'overdue') return <IconCloseButton color={COLORS.white} className={iconClass} />;
    if (taskState === 'current') return <IconCircle className={iconClass} color={COLORS.darkGrey} strokeWidth="2.5" />;
    return <IconFlag className={iconClass} color={COLORS.darkGrey} strokeWidth="2" />;
  };

  const remainingDays = useMemo(() => {
    if (taskState === 'pending' && generatedDate) {
      const now = dayjs();
      const deadline = dayjs(lastAssignedAt).add(7, 'day');
      const oneDay = 24 * 60 * 60 * 1000;
      return Math.max(0, Math.ceil((deadline.valueOf() - now.valueOf()) / oneDay));
    }
    return 0;
  }, [generatedDate, lastAssignedAt, taskState]);

  return (
    <>
      {/* MOBILE VIEW CARD (hidden on desktop, flex on max-width 900px) */}
      <div className="hidden md:flex bg-white rounded-xl shadow-block-shadow overflow-hidden items-stretch justify-between p-0 min-h-[56px]">
        <div className={`flex items-center gap-x-1.5 px-3 py-2.5 rounded-l-xl rounded-r-none ${statusBg()}`}>
          {iconType(true)}
          <span className="font-semibold text-13">W{weekIndex}</span>
        </div>
        <div className="flex flex-col justify-center flex-1 min-w-0 px-2 py-1.5">
          <div className="flex items-center gap-x-1 text-12 font-medium text-black">
            <IconClipboardType className="w-3.5 h-3.5" color={task.task_id ? COLORS.black : COLORS.lightBlue} />
            <span className="truncate">
              {task.task_id ? (
                <>
                  <span className={task.completed ? 'text-green font-bold' : 'text-red font-bold'}>{Number(task.completed)}</span> (1)
                </>
              ) : (
                <span className="text-lightBlue text-11">{taskState === 'current' ? 'No new forms yet' : '-'}</span>
              )}
            </span>
          </div>
          <p className="text-[11px] font-medium truncate">
            {task.completed && <span className="text-green">Completed</span>}
            {taskState === 'current' && <span className="text-blue">Current</span>}
            {taskState === 'upcoming' && <span className="text-lightBlue">Upcoming</span>}
            {taskState === 'pending' && <span className="text-orange">Pending ({remainingDays}d left)</span>}
            {taskState === 'overdue' && <span className="text-red">Overdue</span>}
          </p>
        </div>
        <div className="flex items-center pr-2 flex-shrink-0">{storeTaskButtons}</div>
      </div>

      {/* DESKTOP VIEW TABLE ROW (flex on desktop, hidden on max-width 900px) */}
      <div className="flex md:hidden bg-white rounded-lg shadow-block-shadow h-[76px] overflow-hidden">
        <div className={`flex items-center w-[20%] gap-x-4 px-5 rounded-l-lg rounded-r-none ${statusBg()}`}>
          {iconType(false)}
          <p className="text-24 font-normal whitespace-nowrap">Week {weekIndex}</p>
        </div>
        <div className="w-4/5 flex items-center px-4 gap-x-8">
          <div className={`${task.task_id ? 'w-1/4' : 'w-2/4'} flex items-center gap-x-1`}>
            <IconClipboardType color={task.task_id ? COLORS.black : COLORS.lightBlue} />
            <p className="flex text-16 gap-x-1">
              {task.task_id ? (
                <span>
                  <span className={`${task.completed ? 'text-green' : 'text-red'}`}>{Number(task.completed)}</span> (1)
                </span>
              ) : (
                <span className="text-lightBlue text-14">{taskState === 'current' ? 'No new forms yet' : '-'}</span>
              )}
            </p>
          </div>
          <p className={`text-14 font-medium ${task.task_id ? 'w-1/4' : 'w-0 text-[0]'}`}>
            {weekIndex <= lastAssignedWeek ? granted : '-'}
          </p>
          <p className="text-14 font-medium w-1/4">{generatedDate ? generatedDate.format(DATE_FORMAT) : '-'}</p>
          <p className="text-14 font-medium w-1/4">
            {task.completed && <span className="text-green">Completed</span>}
            {taskState === 'current' && <span className="text-blue">Current</span>}
            {taskState === 'upcoming' && <span className="text-lightBlue">Upcoming</span>}
            {taskState === 'pending' && (
              <span className="text-orange flex items-center gap-x-1">
                Pending <span className="text-lightBlue">({remainingDays} days left)</span>
              </span>
            )}
            {taskState === 'overdue' && <span className="text-red">Overdue</span>}
          </p>
          <div className="w-1/4 flex justify-end">{storeTaskButtons}</div>
        </div>
      </div>
    </>
  );
};
